import { useCallback, useEffect, useRef } from 'react';
import { commentActions } from '../actions';
import { CONTENT_GROUP_ACTION } from '../constants';
import { createCancelToken } from '../utils/Axios';

/**
 * Custom hook to mark a content group as read when it enters the viewport.
 * @param {Object} params - Hook parameters.
 * @param {Function} params.dispatch - Redux dispatch function.
 * @param {string} params.workspace_id - Workspace identifier.
 * @param {string} params.content_group_id - Content group identifier.
 * @param {Object} params.triggerRef - Ref to the DOM element to observe.
 * @param {string} params.activeTab - Currently active tab.
 * @returns {Function} markAsRead - Callback to manually mark as read.
 */
export const useMarkAsReadHook = ({
    dispatch,
    workspace_id,
    content_group_id,
    triggerRef,
    activeTab,
}) => {
    const cancelTokenSourceRef = useRef(createCancelToken());
    const MARK_AS_READ_DELAY_MS = 1000; // 1 second delay

    const markAsRead = useCallback(() => {
        if (content_group_id) {
            const onSuccess = (response) => {
                const { content_group } = response.data;
                dispatch({ type: CONTENT_GROUP_ACTION.UPDATE_CARD_UNREAD_COMMENTS, payload: content_group });
            }

            const onBefore =()=> {
                if (activeTab === 'discussion') {
                    dispatch({ type: CONTENT_GROUP_ACTION.READ_GUEST_COMMENTS });
                }
                else {
                    dispatch({ type: CONTENT_GROUP_ACTION.READ_TEAM_COMMENTS });
                }
            }
            dispatch(commentActions.markAsReadComment({workspace_id, content_group_id, activeTab, onSuccess, onBefore, cancelTokenSource: cancelTokenSourceRef.current.token}));
        }
    }, [dispatch, content_group_id, activeTab]);

    useEffect(() => {
        // Cleanup function to cancel any ongoing requests when the component unmounts
        return () => {
            if (cancelTokenSourceRef.current) {
                cancelTokenSourceRef.current.cancel('Component unmounted, mark as read request cancelled');
            }
        };
    }, []);

    useEffect(() => {
        if (!triggerRef?.current) return;

        let timeoutId = null;

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        // Add a delay before marking as read
                        timeoutId = setTimeout(() => {
                            markAsRead();
                        }, MARK_AS_READ_DELAY_MS);
                    } else {
                        // Clear timeout if the element leaves the viewport before delay
                        if (timeoutId) {
                            clearTimeout(timeoutId);
                            timeoutId = null;
                        }
                    }
                });
            },
            { threshold: 0.1 }
        );

        observer.observe(triggerRef.current);

        return () => {
            observer.disconnect();
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        };
    }, [triggerRef, markAsRead]);

    return markAsRead;
};
